import {
    createChipFormatter,
    createCurrencyFormatter,
    createDateFormatter,
    createExternalLinkFormatter,
    createOptionsFormatter,
    DataTableColumn,
} from '@/components/ui/data-table-customized'
import i18n from '@/configs/i18n'
import { TFunction } from 'i18next'
import { Clock, UserX } from 'lucide-react'
import { Tooltip } from 'vnetwork-ui'
import { ContactSource } from '../../types/response'
import {
    ContactChanel,
    ContactStatusTypes,
    getContactStatusTypesColor,
    Industries,
} from '../../types/utils'
import { getAlertColors, getLeadAlert } from '../../utils/lead-alerts'
import ContactExplaination from './Explaination'

// Alert badge component for list view
const LeadAlertBadge = ({ contact }: { contact: ContactSource }) => {
    const alert = getLeadAlert(contact)
    if (!alert) return null

    const colors = getAlertColors(alert.type)
    if (!colors) return null

    const Icon = alert.type === 'unassigned' ? UserX : Clock

    return (
        <Tooltip
            title={`${i18n.t(alert.message)} (${alert.daysOverdue} ${i18n.t('days')})`}
        >
            <span
                className={`inline-flex items-center gap-1 text-xs ${colors.text}`}
            >
                <Icon size={12} />
                {alert.daysOverdue}d
            </span>
        </Tooltip>
    )
}

export const contactSourceColumns = (
    t: TFunction<'translation', undefined>,
    tGlobal: TFunction<'translation', undefined>,
    _onClick?: (contactSourceId: number) => void
): DataTableColumn<ContactSource>[] => [
    {
        key: 'alert',
        title: '',
        width: 70,
        render: (_, row) => <LeadAlertBadge contact={row} />,
    },
    {
        key: 'contact_source_name',
        title: tGlobal('contact-source-name'),
        width: 200,
        sortable: true,
        render: (_, row) => (
            <span
                className="uppercase truncate cursor-pointer text-primary"
                onClick={() => _onClick?.(row?.contact_source_id)}
            >
                {row?.contact_source_name || ' '}
            </span>
        ),
    },
    {
        key: 'contact_source_position',
        title: tGlobal('position'),
        width: 200,
    },
    {
        key: 'contact_source_phone',
        title: tGlobal('contact-source-phone'),
        width: 150,
    },
    {
        key: 'contact_source_email',
        title: tGlobal('email'),
        width: 250,
        sortable: true,
    },
    {
        key: 'contact_source_profile',
        title: tGlobal('contact-source-profile'),
        width: 250,
        render: (_, row) => {
            const value = row?.contact_source_profile
            return value?.match(/(http|www)/) ? (
                <a
                    href={value}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline text-blue-800"
                >
                    {value}
                </a>
            ) : (
                value
            )
        },
    },
    {
        key: 'contact_source_company',
        title: t('company'),
        width: 200,
        cellClassName: 'truncate',
        sortable: true,
    },
    {
        key: 'contact_source_industry',
        title: tGlobal('contact-source-industry'),
        width: 200,
        formatter: createOptionsFormatter({
            options: Industries,
            renderMode: 'text',
        }),
    },
    {
        key: 'contact_source_website',
        title: 'Website',
        width: 200,
        formatter: createExternalLinkFormatter({
            href: (value) => value,
            target: '_blank',
        }),
    },
    {
        key: 'contact_source_notes',
        title: tGlobal('note'),
        width: 300,
    },
    {
        key: 'website_referrer',
        title: tGlobal('website-referrer'),
        width: 150,
    },
    {
        key: 'source_url',
        title: tGlobal('source-url'),
        width: 250,
        cellClassName: 'truncate',
        render: (_, row) => {
            const sourceUrl = row?.metadata?.source_url
            if (!sourceUrl)
                return (
                    <span className="text-gray-400 italic">
                        {t('un-source-url')}
                    </span>
                )
            return (
                <Tooltip title={sourceUrl}>
                    <a
                        href={sourceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline truncate block"
                    >
                        {sourceUrl}
                    </a>
                </Tooltip>
            )
        },
    },
    {
        key: 'contact_source_channel',
        title: tGlobal('contact-source-channel'),
        width: 200,
        sortable: true,
        formatter: createChipFormatter({
            colorMap: (value) => {
                const channelString = value?.toString()?.toLowerCase()
                if (channelString === 'marketing_approach') {
                    return {
                        label: `Marketing Approach (${t('old')})`,
                        color: 'error',
                    }
                }
                const channel = ContactChanel(t)?.find(
                    (c) => c.value === channelString
                )
                return {
                    label: channel?.label || String(value),
                    color: 'primary',
                }
            },
        }),
    },
    {
        key: 'contact_source_interested_services',
        title: tGlobal('contact-source-interested-services'),
        width: 300,
        cellClassName: 'truncate',
        render: (_, row) => {
            const value = row?.contact_source_interested_services
                ?.map((service) => service)
                ?.join(', ')
            if (!value)
                return (
                    <span className="text-gray-400 italic">
                        {t('un-interested-services')}
                    </span>
                )
            return (
                <Tooltip title={value}>
                    <span className="truncate">{value}</span>
                </Tooltip>
            )
        },
    },
    {
        key: 'contact_source_status',
        title: tGlobal('contact-source-status'),
        width: 200,
        sortable: true,
        formatter: createChipFormatter<ContactSource>({
            colorMap: (value) => {
                if (value?.toString()?.toLowerCase() === 'hr')
                    return { label: 'HR', color: 'info' }
                const status = ContactStatusTypes(i18n.t).find(
                    (s) => s.value === value
                )
                return {
                    label: status?.label || String(value),
                    color: getContactStatusTypesColor(value) as any,
                }
            },
        }),
    },
    {
        key: 'contact_source_customer_journey_status',
        title: tGlobal('journey-status'),
        width: 200,
        cellClassName: 'truncate',
        render: (_, row) => {
            if (!row?.converted) return null
            return (
                <ContactExplaination
                    journeyStep={row?.current_journey_step}
                    journeyStepStatus={row?.current_journey_step_status}
                    lastNote={row?.last_journey_note}
                    lastNoteAt={row?.last_journey_note_at}
                />
            )
        },
    },
    {
        key: 'contact_source_assignee_ids',
        title: t('contact-source-assignee'),
        width: 200,
        render: (_, row) => {
            const value = row?.contact_source_assignee_name
                ?.map((item) => item?.employee_name)
                ?.join(', ')
            if (!value)
                return (
                    <span className="text-gray-400 italic">
                        {t('un-sales-assigned')}
                    </span>
                )
            return (
                <Tooltip title={value}>
                    <span className="truncate">{value}</span>
                </Tooltip>
            )
        },
    },
    {
        key: 'revenue',
        title: t('revenue'),
        width: 150,
        cellClassName: 'text-right',
        formatter: createCurrencyFormatter({ currency: '' }),
    },
    {
        key: 'contact_source_created_at',
        title: t('created-at'),
        width: 200,
        cellClassName: 'text-right',
        sortable: true,
        formatter: createDateFormatter({
            format: 'DD/MM/YYYY HH:mm',
            timezone: 'utc',
        }),
    },
]
